"use client";

import { Check, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Text } from "@/components/primitives";
import AppImage from "@/components/primitives/Image/Image";
import { useAnalytics } from "@/core/analytics";
import { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { useCountdown } from "@/core/hooks";
import { type BCP47LangCode, useI18n, useTranslation } from "@/core/i18n";
import { compressImage } from "@/core/media";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { useAssessmentLogger } from "@/features/assessments/logging/useAssessmentLogger";
import type { CapturePhase } from "@/features/assessments/components/ImageCaptureStep/captureTypes";
import { CAPTURE_COMPRESS_MAX_SIZE, CAPTURE_COMPRESS_QUALITY, CAPTURE_JPEG_QUALITY, FLASH_DELAY_MS } from "@/features/camera/captureConstants";
import { captureFrame } from "@/features/camera/captureFrame";
import { useCameraSession } from "@/features/camera/useCameraSession";
import { useCaptureFeedback } from "@/features/camera/useCaptureFeedback";
import { createFaceCropWithFallback, useProfileAlignment } from "@/features/facialRecognition";
import { uploadProfileFacialImageForAssessment } from "../hooks/photoApi";
import { useResetOnCameraError } from "../hooks/useResetOnCameraError";
import { useWebsiteEmbedCameraBootstrap } from "../hooks/useWebsiteEmbedCameraBootstrap";
import type { ProfileCaptureResult } from "../types";
import { CameraLoadingSkeleton } from "./components/CameraLoadingSkeleton";
import { CameraSwitchButton } from "./components/CameraSwitchButton";
import { CameraSwitchFade } from "./components/CameraSwitchFade";
import { CaptureCountdownOverlay } from "./components/CaptureCountdownOverlay";
import { CaptureErrorOverlay } from "./components/CaptureErrorOverlay";
import { CaptureFaceGuide } from "./components/CaptureFaceGuide";
import { CaptureFlashOverlay } from "./components/CaptureFlashOverlay";
import { CaptureFooterControls } from "./components/CaptureFooterControls";
import { CaptureHeader } from "./components/CaptureHeader";
import { CaptureIntroOverlay } from "./components/CaptureIntroOverlay";
import { CaptureShutterButton } from "./components/CaptureShutterButton";
import { CaptureViewportFrame } from "./components/CaptureViewportFrame";
import { CaptureZoomControls } from "./components/CaptureZoomControls";
import { CaptureValidationActions } from "./components/CaptureValidationActions";
import { ProfileDirectionArrow } from "./components/ProfileDirectionArrow";
import { WebsiteEmbedCameraStartOverlay } from "./components/WebsiteEmbedCameraStartOverlay";
type ProfileCaptureStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  platformId: string;
  onComplete: (result: ProfileCaptureResult) => void;
  onSwitchToUpload?: () => void;
  skipIntro?: boolean;
};
type ProfileSubPhase = "direction" | "countdown" | "review";
const PROFILE_COUNTDOWN_SECONDS = 3;
const PROFILE_INTRO_HOLD_MS = 600;
const PROFILE_DIRECTION_SETTLE_MS = 300;
const LOOK_RIGHT_AUDIO_PATHS: Record<BCP47LangCode, string> = {
  "en-US": "/audio/look-right/look-right-en_US.mp3",
  "en-CA": "/audio/look-right/look-right-en_US.mp3",
  "es-MX": "/audio/look-right/look-right-es_MX.mp3",
  "fr-CA": "/audio/look-right/look-right-fr_CA.mp3",
  "en-BR": "/audio/look-right/look-right-en_US.mp3",
  // No pt_BR recording yet — falls back to the en_US clip until one is produced.
  "pt-BR": "/audio/look-right/look-right-en_US.mp3"
};
const LOOK_RIGHT_FALLBACK_LANG: BCP47LangCode = "en-US";
const DEFAULT_DIRECTION: "left" | "right" = "right";
const PROFILE_INTRO_MESSAGE = "Now let\u2019s capture your side profile.";
export function ProfileCaptureStage({
  assessment,
  headers,
  platformId,
  onComplete,
  onSwitchToUpload,
  skipIntro = false
}: ProfileCaptureStageProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const {
    currentLang
  } = useI18n();
  const analytics = useAnalytics();
  const {
    flowType
  } = useAssessmentStep();
  const logger = useAssessmentLogger(assessment.id, {
    stage: "profile_capture"
  });
  const {
    triggerBeep,
    triggerShutter
  } = useCaptureFeedback();

  // ProfileCaptureStage owns this flow state machine. It is deliberately kept
  // orthogonal to camera health:
  //   - subPhase describes where the user is in the capture sequence
  //     (direction → countdown → review).
  //   - errorKind (from useCameraSession) describes camera session health.
  // Both must be consulted when gating UI. Do not fold an "error" / "blocked"
  // member into subPhase — that would create two sources of truth for camera
  // health and require a sync effect to keep them consistent. The
  // isCameraBlocked-guarded footer branches further down are the intended
  // idiom.
  const [subPhase, setSubPhase] = useState<ProfileSubPhase>("direction");
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [capturedSide, setCapturedSide] = useState<"left" | "right">(DEFAULT_DIRECTION);
  const [showFlash, setShowFlash] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedImageId, setUploadedImageId] = useState<string | null>(null);
  const [introComplete, setIntroComplete] = useState(skipIntro);
  const [cameraReady, setCameraReady] = useState(false);
  const hasPlayedLookRightAudioRef = useRef(false);
  const lookRightAudioRef = useRef<HTMLAudioElement | null>(null);
  const profilePhase: CapturePhase = subPhase === "direction" ? capturedSide === "right" ? "profile-right-direction" : "profile-left-direction" : subPhase === "countdown" ? capturedSide === "right" ? "profile-right-countdown" : "profile-left-countdown" : "profile-review";

  // Camera session, deliberately decoupled from this stage's flow state
  // machine — see the orthogonality note on the `subPhase` declaration
  // above. We observe camera health via `errorKind` and never let the hook
  // write into our local state, so `setPhase` is omitted.
  const {
    videoRef,
    canvasRef,
    streamRef,
    errorKind,
    facingMode,
    startCamera,
    switchCamera
  } = useCameraSession({
    onCameraError: (error, kind) => {
      logger.error("camera_session_start_failed", {
        errorKind: kind
      }, error);
    }
  });
  const {
    needsEmbedCameraTap,
    beginEmbedCameraSession
  } = useWebsiteEmbedCameraBootstrap(startCamera);
  const isFrontCamera = facingMode === "user";
  const [isSwitching, setIsSwitching] = useState(false);
  const {
    isGuidanceAvailable,
    leftProfileReady,
    rightProfileReady,
    isAlignmentStuck,
    getDetectedProfileSide,
    lastLandmarksRef
  } = useProfileAlignment({
    phase: profilePhase,
    videoRef,
    onStuckTimeoutFired: stuckPhase => {
      logger.warn("profile_alignment_stuck_manual_capture_enabled", {
        phase: stuckPhase
      });
    }
  });

  // Extracted so "retry upload" (see CaptureValidationActions) can
  // reuse the already-captured bytes instead of forcing a full retake.
  const runUpload = useCallback(async (processed: string, detectedSide: "left" | "right") => {
    setIsUploading(true);
    setUploadError(null);
    setUploadedImageId(null);
    try {
      const uploadResponse = await uploadProfileFacialImageForAssessment({
        assessmentId: assessment.id,
        platformId,
        photoDataUrl: processed,
        requestOptions: {
          headers
        }
      });
      setUploadedImageId(uploadResponse.imageId);
      analytics.trackEvent("assessment_profile_photo_captured", {
        flowType,
        side: detectedSide
      });
    } catch (error_0) {
      logger.error("profile_capture_upload_failed", {
        side: detectedSide
      }, error_0 as Error);
      setUploadError(t("assessmentSteps.studioSession.profiles.uploadFailed"));
    } finally {
      setIsUploading(false);
    }
  }, [assessment.id, platformId, headers, analytics, flowType, logger, t]);
  const handleRetryUpload = useCallback(async () => {
    if (!capturedPhoto) return;
    await runUpload(capturedPhoto, capturedSide);
  }, [capturedPhoto, capturedSide, runUpload]);
  const handleCaptureComplete = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      setSubPhase("direction");
      return;
    }
    setShowFlash(true);
    await new Promise(resolve => setTimeout(resolve, FLASH_DELAY_MS));
    triggerShutter();
    const landmarks = lastLandmarksRef.current;
    const rawPhoto = captureFrame({
      video,
      canvas,
      quality: CAPTURE_JPEG_QUALITY,
      mirror: isFrontCamera
    });
    setShowFlash(false);
    if (!rawPhoto) {
      setSubPhase("direction");
      return;
    }
    let processed_0 = rawPhoto;
    try {
      processed_0 = await createFaceCropWithFallback(rawPhoto, landmarks);
      processed_0 = await compressImage(processed_0, CAPTURE_COMPRESS_MAX_SIZE, CAPTURE_COMPRESS_QUALITY);
    } catch (error_1) {
      logger.error("profile_capture_compress_failed", undefined, error_1 as Error);
    }
    const detectedSide_0 = getDetectedProfileSide() ?? capturedSide;
    setCapturedSide(detectedSide_0);
    setCapturedPhoto(processed_0);
    setSubPhase("review");
    await runUpload(processed_0, detectedSide_0);
  }, [videoRef, canvasRef, lastLandmarksRef, isFrontCamera, triggerShutter, getDetectedProfileSide, capturedSide, runUpload, logger]);
  const {
    countdown,
    startCountdown,
    cancelCountdown
  } = useCountdown({
    onTick: triggerBeep,
    onComplete: () => {
      void handleCaptureComplete();
    }
  });
  useEffect(() => {
    const audio = new Audio(getLookRightAudioUrl(currentLang));
    audio.preload = "auto";
    lookRightAudioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = "";
    };
  }, [currentLang]);
  const resetDirectionPromptState = useCallback(() => {
    hasPlayedLookRightAudioRef.current = false;
    const audio_0 = lookRightAudioRef.current;
    if (!audio_0) return;
    audio_0.pause();
    audio_0.currentTime = 0;
  }, []);
  useEffect(() => {
    if (hasPlayedLookRightAudioRef.current) return;
    if (!introComplete) return;
    if (subPhase !== "direction") return;
    const audio_1 = lookRightAudioRef.current;
    let isWaitingForUserGesture = false;
    const clearGestureRetryListeners = () => {
      if (!isWaitingForUserGesture) return;
      document.removeEventListener("touchstart", handleGestureRetry);
      document.removeEventListener("click", handleGestureRetry);
      isWaitingForUserGesture = false;
    };
    const attemptPlayback = () => {
      if (!audio_1) return;
      void audio_1.play().then(() => {
        hasPlayedLookRightAudioRef.current = true;
        clearGestureRetryListeners();
      }, () => {
        if (isWaitingForUserGesture) return;
        isWaitingForUserGesture = true;
        document.addEventListener("touchstart", handleGestureRetry, {
          once: true
        });
        document.addEventListener("click", handleGestureRetry, {
          once: true
        });
      });
    };
    const handleGestureRetry = () => {
      clearGestureRetryListeners();
      attemptPlayback();
    };
    if (!audio_1) {
      hasPlayedLookRightAudioRef.current = true;
      return;
    }
    attemptPlayback();
    return () => {
      clearGestureRetryListeners();
    };
  }, [introComplete, subPhase]);
  useEffect(() => {
    if (!introComplete) return;
    if (subPhase !== "direction") return;
    const detectedSide_1 = getDetectedProfileSide();
    const isReady = leftProfileReady || rightProfileReady;
    if (!isReady) return;
    const settleTimer = window.setTimeout(() => {
      const nextDetectedSide = getDetectedProfileSide() ?? detectedSide_1;
      if (nextDetectedSide) {
        setCapturedSide(nextDetectedSide);
      }
      const didStartCountdown = startCountdown(PROFILE_COUNTDOWN_SECONDS);
      if (!didStartCountdown) return;
      setSubPhase("countdown");
    }, PROFILE_DIRECTION_SETTLE_MS);
    return () => {
      window.clearTimeout(settleTimer);
    };
  }, [introComplete, subPhase, rightProfileReady, leftProfileReady, getDetectedProfileSide, startCountdown]);
  const isAligned = capturedSide === "right" ? rightProfileReady : leftProfileReady;

  // Allow manual capture when guidance is unavailable OR when the alignment
  // stuck-timeout has fired. Without the second condition users whose yaw
  // never trips the threshold (insufficient neck mobility, tight framing,
  // poor landmark detection lighting) had no escape path.
  const canManualCapture = !isGuidanceAvailable || isAligned || isAlignmentStuck;
  const handleManualCapture = useCallback(() => {
    if (subPhase !== "direction" || countdown !== null) return;
    if (!canManualCapture) return;
    const didStartCountdown_0 = startCountdown(PROFILE_COUNTDOWN_SECONDS);
    if (!didStartCountdown_0) return;
    setSubPhase("countdown");
  }, [subPhase, countdown, canManualCapture, startCountdown]);
  const handleRetake = useCallback(() => {
    analytics.trackEvent("assessment_photo_retake_requested", {
      assessmentId: assessment.id,
      flowType,
      photoType: "profile",
      source: "profile_capture_review"
    });
    resetDirectionPromptState();
    setCapturedPhoto(null);
    setUploadError(null);
    setUploadedImageId(null);
    setCameraReady(false);
    setSubPhase("direction");
    void startCamera();
  }, [analytics, assessment.id, flowType, resetDirectionPromptState, startCamera]);
  const handleSwitchCamera = useCallback(async () => {
    cancelCountdown();
    if (subPhase === "countdown") {
      setSubPhase("direction");
    }
    setIsSwitching(true);
    setCameraReady(false);
    await switchCamera();
    setIsSwitching(false);
  }, [cancelCountdown, subPhase, switchCamera]);
  const handleConfirm = useCallback(() => {
    if (!capturedPhoto || !uploadedImageId) return;
    onComplete({
      profilePhotoData: capturedPhoto,
      profileSide: capturedSide,
      profileImageId: uploadedImageId
    });
  }, [capturedPhoto, uploadedImageId, onComplete, capturedSide]);
  const directionLabel = capturedSide === "right" ? t("assessmentSteps.studioSession.profiles.turnRight") : t("assessmentSteps.studioSession.profiles.turnLeft");
  const guideColor = isAligned ? "hsl(var(--primary))" : "rgba(255, 255, 255, 0.4)";
  const guideGlowColor = isAligned ? "hsl(var(--primary) / 0.3)" : "rgba(255, 255, 255, 0.2)";
  const showProfileGuide = subPhase !== "review";
  const canAdvance = Boolean(capturedPhoto && uploadedImageId && !isUploading && !uploadError);
  const handleCancelCountdown = useCallback(() => {
    cancelCountdown();
    setSubPhase("direction");
  }, [cancelCountdown]);
  useEffect(() => {
    if (subPhase !== "review" || !canAdvance) return;
    handleConfirm();
  }, [subPhase, canAdvance, handleConfirm]);

  // When the camera fails mid-flow (permission revoked from browser settings,
  // OS-level camera usage, etc.), collapse any in-flight countdown, clear
  // transient flash state, and return to the direction subPhase so the
  // CaptureErrorOverlay is the only active UI. Without this the countdown
  // keeps beeping, eventually fires handleCaptureComplete on a dead stream,
  // and captureFrame returns null — users would hear beeps then silently
  // land back on the direction screen with the error overlay covering a
  // stale guidance state.
  //
  // Scoped to non-review subPhases: once the user has captured and is
  // reviewing their photo, losing the camera is irrelevant to that screen's
  // validity.
  const isCameraBlocked = errorKind !== null;
  const resetOnCameraError = useCallback(() => {
    if (subPhase === "review") return;
    cancelCountdown();
    setShowFlash(false);
    if (subPhase !== "direction") {
      setSubPhase("direction");
    }
  }, [subPhase, cancelCountdown]);
  useResetOnCameraError({
    errorKind,
    onReset: resetOnCameraError
  });
  return <div className="relative flex min-h-[100dvh] w-full flex-1 flex-col overflow-hidden bg-black">
      <CaptureHeader title={t("assessmentSteps.studioSession.profiles.profileLabel")} />

      {/* Camera viewport */}
      {subPhase !== "review" && <CaptureViewportFrame>
          {needsEmbedCameraTap ? <WebsiteEmbedCameraStartOverlay onStart={beginEmbedCameraSession} /> : null}

          {!cameraReady && !isSwitching && !needsEmbedCameraTap && <CameraLoadingSkeleton />}

          <CameraSwitchFade isSwitching={isSwitching} />

          <video autoPlay muted playsInline className={`h-full w-full object-cover transition-opacity duration-500 ${isFrontCamera ? "scale-x-[-1]" : ""} ${cameraReady ? "opacity-100" : "opacity-0"}`} ref={videoRef} onPlaying={() => setCameraReady(true)} />
          <canvas className="hidden" ref={canvasRef} />

          {!isFrontCamera && cameraReady && <CaptureZoomControls streamRef={streamRef} videoRef={videoRef} />}

          {/* Profile guide overlay */}
          <AnimatePresence>
            {showProfileGuide && <motion.div aria-hidden animate={{
          opacity: 1
        }} className="pointer-events-none absolute inset-0 flex items-center justify-center" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }}>
                <CaptureFaceGuide glowColor={guideGlowColor} horizontalLineEmphasis={isAligned ? 0.35 : 0.55} isAligned={isAligned} ringColor={guideColor} variant={capturedSide === "right" ? "profile-right" : "profile-left"} verticalLineEmphasis={capturedSide === "right" ? 0.7 : 0.45} />
              </motion.div>}
          </AnimatePresence>

          <CaptureCountdownOverlay countdown={countdown} />

          <CaptureFlashOverlay showFlash={showFlash} />

          <CaptureErrorOverlay errorKind={errorKind} onRetry={() => startCamera()} onUploadInstead={onSwitchToUpload} />
        </CaptureViewportFrame>}

      {/* Review preview */}
      {subPhase === "review" && capturedPhoto && <div className="relative flex min-h-0 flex-1 items-start justify-center px-3 py-1 sm:px-6">
          <div className="relative aspect-3/4 w-full max-w-md overflow-hidden rounded-3xl">
            <AppImage fill alt={t("assessmentSteps.studioSession.profiles.profileLabel")} className="object-cover" sizes="(max-width: 640px) 100vw, 480px" src={capturedPhoto} />
          </div>
        </div>}

      {/* Footer. Direction and countdown footers are suppressed while the
       * camera is blocked so the CaptureErrorOverlay (rendered inside the
       * viewport) is the only actionable UI — otherwise users saw the
       * shutter button and "Turn RIGHT" guidance rendered below a blocked
       * camera, which made the error state feel recoverable via a button
       * click that couldn't actually capture anything. They are likewise
       * suppressed while needsEmbedCameraTap is true (website embeds, before
       * the in-iframe "Enable camera" tap starts getUserMedia) so the
       * WebsiteEmbedCameraStartOverlay is the only actionable UI — otherwise
       * a visitor could start a countdown/capture on a camera that was never
       * started. Review footer is unconditionally rendered because by then
       * the photo already exists and camera state is irrelevant to
       * finish/retake/retry-upload. */}
      <div className="pb-safe relative z-10 w-full bg-black px-4 pt-3 pb-4 sm:px-6">
        <AnimatePresence mode="wait">
          {subPhase === "direction" && !isCameraBlocked && !needsEmbedCameraTap && <motion.div animate={{
          opacity: 1
        }} className="flex flex-col items-center gap-3 text-center" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }} key="direction" transition={{
          duration: 0.25
        }}>
              <ProfileDirectionArrow direction={capturedSide} isAligned={isAligned} />
              <div>
                <Text component="p" text={directionLabel} tone="muted" variant="body-md" weight="light" />
                {isGuidanceAvailable && !isAlignmentStuck && <div className="mt-1">
                    <Text component="p" text={isAligned ? t("assessmentSteps.studioSession.profiles.ready") : t("assessmentSteps.studioSession.profiles.alignToStart")} tone="muted" variant="caption" />
                  </div>}
              </div>
              {isGuidanceAvailable && <div className="flex justify-center gap-4">
                  <div className={`type-caption flex items-center gap-1.5 ${isAligned ? "text-white/90" : "text-white/40"}`}>
                    <div className="h-1.5 w-1.5 rounded-full transition-colors duration-300" style={{
                backgroundColor: guideColor
              }} />
                    <Text component="span" text={t("assessmentSteps.studioSession.guidance.faceAligned")} variant="caption" />
                  </div>
                  <div className={`type-caption flex items-center gap-1.5 ${isAligned ? "text-white/90" : "text-white/50"}`}>
                    {isAligned ? <Check className="h-3 w-3" style={{
                color: guideColor
              }} /> : <div className="h-1.5 w-1.5 rounded-full bg-white/40" />}
                    <Text component="span" text={directionLabel} variant="caption" />
                  </div>
                </div>}
              <CaptureFooterControls onSwitchCamera={handleSwitchCamera} shutter={<CaptureShutterButton boxShadow={`0 0 0 4px ${guideColor}, 0 0 20px ${guideGlowColor}`} disabled={countdown !== null || !canManualCapture} onCapture={handleManualCapture} />} />
            </motion.div>}

          {subPhase === "countdown" && !isCameraBlocked && !needsEmbedCameraTap && <motion.div animate={{
          opacity: 1
        }} className="flex flex-col items-center gap-3 text-center" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }} key="countdown" transition={{
          duration: 0.2
        }}>
              {countdown !== null ? <Text component="p" text={t("assessmentSteps.studioSession.profiles.capturingProfile")} variant="body-sm" /> : <Loader2 className="h-6 w-6 animate-spin text-white/60" />}
              <button className="type-body-sm text-white/60 active:text-white/90" type="button" onClick={handleCancelCountdown}>
                {t("assessmentSteps.studioSession.cancelCountdown")}
              </button>
              <div className="flex w-full items-center justify-center">
                <div className="w-12 shrink-0" />
                <div className="flex-1" />
                <div className="flex w-12 shrink-0 justify-center">
                  <CameraSwitchButton onSwitch={handleSwitchCamera} />
                </div>
              </div>
            </motion.div>}

          {subPhase === "review" && <motion.div animate={{
          opacity: 1
        }} className="flex flex-col items-center gap-3" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }} key="review" transition={{
          duration: 0.25
        }}>
              {isUploading && <Loader2 className="h-6 w-6 animate-spin text-white/70" />}
              {uploadError && !isUploading && <CaptureValidationActions isBlocked canAdvance={false} errorMessage={uploadError} onAdvance={handleConfirm} onRetake={handleRetake} onRetryUpload={handleRetryUpload} />}
            </motion.div>}
        </AnimatePresence>
      </div>
      {!skipIntro && <CaptureIntroOverlay durationMs={PROFILE_INTRO_HOLD_MS} message={PROFILE_INTRO_MESSAGE} onComplete={() => setIntroComplete(true)} />}
    </div>;
}
function getLookRightAudioUrl(lang: string): string {
  if (isSupportedLookRightAudioLang(lang)) {
    return LOOK_RIGHT_AUDIO_PATHS[lang];
  }
  return LOOK_RIGHT_AUDIO_PATHS[LOOK_RIGHT_FALLBACK_LANG];
}
function isSupportedLookRightAudioLang(lang: string): lang is BCP47LangCode {
  return Object.prototype.hasOwnProperty.call(LOOK_RIGHT_AUDIO_PATHS, lang);
}